import {PageableFacade} from '../../../../../core/abstracts/pageable-facade.abstract';
import {BehaviorSubject, catchError, finalize, forkJoin, map, of, Subject, tap} from 'rxjs';
import {PageableRequest} from '../../../../../core/models/pagable.model';
import {VehicleSearch} from '../models/vehicle-search.model';
import {computed, Injectable, signal} from '@angular/core';
import {VehicleSearchFilterRequest} from '../models/vehicle-search-filter-request.model';
import {VehicleListService} from '../services/vehicle-list.service';
import {VehicleSearchResponseMapper} from '../mappers/vehicle-search-response.mapper';
import {Page} from '../../../../../core/models/page-response.model';
import {VehicleBrandService} from '../../../brand/services/vehicle-brand.service';
import {VehicleBrandResponseMapper} from '../../../brand/mapper/vehicle-brand-response.mapper';
import {Brand} from '../../../brand/models/brand.model';
import {VehicleModelService} from '../../../model/service/vehicle-model.service';
import {VehicleModelsResponseMapper} from '../../../model/mapper/vehicle-models-response.mapper';
import {VehicleModel} from '../../../model/model/vehicle-model.model';
import {TableLazyLoadEvent} from 'primeng/table';

@Injectable({providedIn: 'root'})
export class VehicleListFacade extends PageableFacade {
  private readonly itemsSubject = new BehaviorSubject<VehicleSearch[]>([]);
  readonly items$ = this.itemsSubject.asObservable();

  private readonly vehicleBrandsSubject: BehaviorSubject<Brand[]> = new BehaviorSubject<Brand[]>([]);
  readonly vehicleBrands$ = this.vehicleBrandsSubject.asObservable();

  private readonly vehicleModelsSubject: BehaviorSubject<VehicleModel[]> = new BehaviorSubject<VehicleModel[]>([]);
  readonly vehicleModels$ = this.vehicleModelsSubject.asObservable();

  public readonly lazyLoadTableSubject = new Subject<TableLazyLoadEvent>();

  private readonly errorSubject = new Subject<Error | null>();
  readonly error$ = this.errorSubject.asObservable();

  private readonly activeFiltersSignal = signal<VehicleSearchFilterRequest>({} as VehicleSearchFilterRequest);
  readonly activeFilters = this.activeFiltersSignal.asReadonly();

  readonly isEmpty = computed(() =>
    this.itemsSubject.value.length === 0 && !this.isLoading()
  );

  readonly hasData = computed(() =>
    this.itemsSubject.value.length > 0
  );

  constructor(
    private readonly vehicleService: VehicleListService,
    private readonly responseMapper: VehicleSearchResponseMapper,
    private readonly vehicleBrandService: VehicleBrandService,
    private readonly vehicleBrandResponseMapper: VehicleBrandResponseMapper,
    private readonly vehicleModelService: VehicleModelService,
    private readonly vehicleModelResponseMapper: VehicleModelsResponseMapper
  ) {
    super();
  }

  override loadPage(pageable: PageableRequest, filters?: VehicleSearchFilterRequest): void {
    this.startLoading('Wczytywanie pojazdów...');

    const currentFilters = filters || this.activeFilters();

    this.vehicleService.getPage(pageable, currentFilters)
      .pipe(
        map(response => ({
          page: Page.fromPaginationResponse(response),
          models: this.responseMapper.mapArray(response.content)
        })),
        tap(({page, models}) => {
          this.itemsSubject.next(models);
          this.updatePaginationFromPage(page);

          if (filters) {
            this.activeFiltersSignal.set(filters);
          }
        }),
        catchError(error => {
          console.error('VehicleListFacade error:', error);
          this.errorSubject.next(new Error('Nie udało się załadować pojazdów'));
          return of(null);
        }),
        finalize(() => this.stopLoading()))
      .subscribe();
  }

  public fetchVehicleBrands(): void {
    this.vehicleBrandService.getAll().subscribe({
      next: (vehicleBrandsResponse) => {
        const vehicleBrands = this.vehicleBrandResponseMapper.mapArray(vehicleBrandsResponse);
        this.vehicleBrandsSubject.next(vehicleBrands);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchVehicleModels(brandIds: number[]): void {
    if (!brandIds.length) {
      this.vehicleModelsSubject.next([]);
      return;
    }

    forkJoin(
      brandIds.map(id => this.vehicleModelService.getById(id))
    ).subscribe({
      next: (responses) => {
        const vehicleModels = responses.flatMap(
          response => this.vehicleModelResponseMapper.mapArray(response)
        );
        this.vehicleModelsSubject.next(vehicleModels);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public clearVehicleModels(): void {
    this.fetchVehicleModels([])
  }

  public applyFilters(filters: VehicleSearchFilterRequest): void {
    this.activeFiltersSignal.set(filters);
    this.loadFirstPage(filters);
  }

  public clearFilters(): void {
    this.activeFiltersSignal.set({} as VehicleSearchFilterRequest);
    this.loadFirstPage();
  }

  public clearError(): void {
    this.errorSubject.next(null);
  }
}
